import { createSlice } from "@reduxjs/toolkit";
import { AibleClientFactory } from "client/aible/factory";
import * as globalReducer from "containers/App/reducer";
import * as accountsReducer from "containers/Accounts/reducer";
const client = AibleClientFactory.getInstance().getContentGenerationClient();
function updateStateWithFineTuning(state, action) {
    return {
        ...state,
        executions: {
            ...state.executions,
            [action.id]: action
        }
    };
}
function updateStateWithFineTuningDetails(state, action) {
    return {
        ...state,
        executions: {
            ...state.executions,
            [action.id]: {
                ...state.executions[action.id],
                details: action.details
            }
        }
    };
}
function deleteFineTuningFromState(state, id) {
    const newState = {
        ...state,
        executions: {
            ...state.executions
        }
    };
    delete newState.executions[id];
    return newState;
}
function updateStateWithExecutionQuery(state, { query, ids }) {
    const queryStr = JSON.stringify(query);
    return {
        ...state,
        queries: {
            ...state.queries,
            executions: {
                ...state.queries.executions,
                [queryStr]: ids
            }
        }
    };
}
const initialState = {
    executions: {},
    queries: {
        executions: {}
    }
};
const fineTuningSlice = createSlice({
    name: "fineTuning",
    initialState,
    reducers: {
        updateFineTuning(state, { payload }) {
            return updateStateWithFineTuning(state, payload);
        },
        updateFineTunings(state, { payload }) {
            return payload.reduce(updateStateWithFineTuning, state);
        },
        deleteFineTuning(state, { payload }) {
            return deleteFineTuningFromState(state, payload);
        },
        updateFineTuningDetails(state, { payload }) {
            return updateStateWithFineTuningDetails(state, payload);
        }
    }
});
export const { updateFineTuning, updateFineTunings, deleteFineTuning, updateFineTuningDetails: updateFineTuningDetailsDispatch } = fineTuningSlice.actions;
export default fineTuningSlice.reducer;
function getBase(state) {
    return state.fineTuning;
}
export function getFineTunings(state) {
    return getBase(state).executions;
}
export function getFineTuningById(state, id) {
    return getFineTunings(state)[id];
}
export function getFineTuningByQuery(state, query) {
    const queryStr = JSON.stringify(query);
    const executionIds = getBase(state).queries.executions[queryStr];
    return executionIds ?? [];
}
export function createFineTuningExecution(post) {
    return async (dispatch, getState) => {
        const accountId = accountsReducer.getSelectedAccountId(getState());
        const factory = await AibleClientFactory.getInstance().getAccountClient().getCustomerClientFactory(accountId);
        const execution = await factory.getContentGenerationClient().createFineTuningExecution(post);
        console.log("createFineTuningExecution execution", execution);
        dispatch(updateFineTuning(execution));
        return execution;
    };
}
export function trainFineTuningExecution({ base_model_version_id, example_set_id, name, description = "", type, parameters }) {
    const payload = {
        base_model_version_id: base_model_version_id,
        example_set_id: example_set_id,
        name,
        description,
        type
    };
    if (parameters !== undefined) {
        payload.parameters = parameters;
    }
    return createFineTuningExecution(payload);
}
export function updateFineTuningExecution(id, post) {
    return async (dispatch) => {
        const execution = await client.updateFineTuningExecution(id, post);
        dispatch(updateFineTuning(execution));
        return execution;
    };
}
export function fetchFineTuningExecutions(query) {
    return async (dispatch) => {
        dispatch(globalReducer.updateIsFineTuningLoading(true));
        const executions = await client.getFineTuningExecutions(query);
        dispatch(updateFineTunings(executions));
        dispatch(globalReducer.updateIsFineTuningLoading(false));
        return executions;
    };
}
export function pollFineTuningExecutions(max_poll = 5) {
    return async (dispatch, getState) => {
        const executions = getInProgressFineTuningExecutions(getFineTunings(getState()));
        // TODO: We may want to change this to Promise.allSettled so we can process which requests were successful and handle any that didn't rather than immediately fail if any request has failed.
        return Promise.all(Iterator.from(executions)
            .take(max_poll)
            .map(async (execution) => {
            const res = await dispatch(fetchFineTuningById(execution.id, false));
            if (execution.status !== res.status) {
                dispatch(updateFineTuning(res));
            }
            return res;
        })
            .toArray());
    };
}
export function fetchFineTuningById(id, update = true) {
    return async (dispatch, getState) => {
        const accountId = accountsReducer.getSelectedAccountId(getState());
        const factory = await AibleClientFactory.getInstance().getAccountClient().getCustomerClientFactory(accountId);
        const execution = await factory.getContentGenerationClient().getFineTuningExecution(id);
        if (update) {
            dispatch(updateFineTuning(execution));
        }
        return execution;
    };
}
export function addFineTuningDetails(id) {
    return async (dispatch, getState) => {
        const accountId = accountsReducer.getSelectedAccountId(getState());
        const factory = await AibleClientFactory.getInstance().getAccountClient().getCustomerClientFactory(accountId);
        const executionDetails = await factory.getContentGenerationClient().getFineTuningExecutionDetails(id);
        dispatch(updateFineTuningDetailsDispatch({ id, details: executionDetails }));
    };
}
export function deleteFineTuningExecution(id) {
    return async (dispatch) => {
        const execution = await client.deleteFineTuningExecution(id);
        if (execution.deleted === "DELETED") {
            dispatch(deleteFineTuning(id));
        }
    };
}
export function cancelFineTuningExecution(id) {
    return async (dispatch, getState) => {
        const accountId = accountsReducer.getSelectedAccountId(getState());
        const factory = await AibleClientFactory.getInstance().getAccountClient().getCustomerClientFactory(accountId);
        const execution = await factory.getContentGenerationClient().cancelFineTuningExecution(id);
        const fetched = dispatch(fetchFineTuningById(id));
        return execution;
    };
}
export function getExecutionCostEstimate(baseModelVersionId, exampleSetId, name, params) {
    return async (dispatch, getState) => {
        const accountId = accountsReducer.getSelectedAccountId(getState());
        const factory = await AibleClientFactory.getInstance().getAccountClient().getCustomerClientFactory(accountId);
        const payload = {
            base_model_version_id: baseModelVersionId,
            example_set_id: exampleSetId,
            name: name,
            description: ""
        };
        if (params !== undefined) {
            payload.parameters = params;
        }
        const execution = await factory.getContentGenerationClient().getExecutionCostEstimate(payload);
        console.log(execution);
        return execution;
    };
}
// Helper functions
// TODO: As far as I know there is no paradigm for helper functions for selectors and we are trying to avoid using filters on selectors as they always return a new array and force a render.
export function getFineTuningExecutionList(executions, withStatus) {
    const values = Object.values(executions);
    if (withStatus === undefined) {
        return values;
    }
    const statusSet = new Set(withStatus);
    return values.filter((execution) => statusSet.has(execution.status));
}
export function getInProgressFineTuningExecutions(executions) {
    return getFineTuningExecutionList(executions, ["RUNNING", "PENDING", "CANCELLING"]);
}
export function getCompletedFineTuningExecutions(executions) {
    return getFineTuningExecutionList(executions, ["SUCCEEDED"]);
}
